/**
 * Utility functions for RSSio
 */

// Helper function to add target="_blank" to all links in HTML content
export function processArticleLinks(htmlContent: string): string {
  if (!htmlContent) return htmlContent;

  // Parse HTML and add target="_blank" and rel="noopener noreferrer" to all links
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  // Find all anchor tags
  const links = doc.querySelectorAll("a");
  links.forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });

  // Return the modified HTML
  return doc.body.innerHTML;
}

export function removeYouTubeEmbeds(htmlContent: string): string {
  if (!htmlContent) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  doc
    .querySelectorAll("iframe, embed, object, lite-youtube")
    .forEach((node) => {
      const src =
        node.getAttribute("src") ||
        node.getAttribute("data-src") ||
        node.getAttribute("href") ||
        "";
      const lowerSrc = src.toLowerCase();
      if (
        lowerSrc.includes("youtube.com") ||
        lowerSrc.includes("youtu.be") ||
        lowerSrc.includes("youtube-nocookie.com")
      ) {
        node.remove();
      }
    });

  doc.querySelectorAll("blockquote").forEach((node) => {
    const className = (node.getAttribute("class") || "").toLowerCase();
    const cite = (node.getAttribute("cite") || "").toLowerCase();
    if (
      className.includes("instagram-media") ||
      className.includes("twitter-tweet") ||
      className.includes("tiktok-embed")
    ) {
      return;
    }
    if (cite.includes("youtube.com") || cite.includes("youtu.be")) {
      node.remove();
    }
  });

  doc.querySelectorAll("a[href]").forEach((link) => {
    const href = (link.getAttribute("href") || "").toLowerCase();
    const text = (link.textContent || "").trim().toLowerCase();
    if (
      (href.includes("youtube.com/embed/") || href.includes("youtu.be/")) &&
      !text
    ) {
      link.remove();
    }
  });

  return doc.body.innerHTML;
}

export function extractImageUrlsFromHtml(htmlContent: string): string[] {
  if (!htmlContent) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");
  const urls = new Set<string>();

  doc.querySelectorAll("img[src]").forEach((img) => {
    const src = img.getAttribute("src") || "";
    if (src.trim()) {
      urls.add(src.trim());
    }
  });

  return Array.from(urls);
}

export function replaceImageSources(
  htmlContent: string,
  replacements: Record<string, string>,
): string {
  if (!htmlContent) return htmlContent;
  if (!replacements || Object.keys(replacements).length === 0) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  doc.querySelectorAll("img[src]").forEach((img) => {
    const src = img.getAttribute("src") || "";
    const replacement = replacements[src];
    if (replacement) {
      img.setAttribute("src", replacement);
    }
  });

  return doc.body.innerHTML;
}

export interface ArticleIdentifiable {
  id?: string;
  guid?: unknown;
  link?: unknown;
  title?: string | unknown;
  pubDate?: string | unknown;
}

// Normalize article ID to a consistent string format
export function normalizeArticleId(item: ArticleIdentifiable | null | undefined): string {
  if (!item) return "";
  let id = item.guid ?? item.id ?? item.link ?? "";

  // Arrays must be flattened FIRST: typeof [] === "object" so the next
  // branch would otherwise reduce them to "" before the Array.isArray
  // check had a chance to run.
  if (Array.isArray(id)) {
    const first = id[0];
    if (first && typeof first === "object") {
      const obj = first as { href?: string; $?: { href?: string } };
      id = obj.href || obj.$?.href || "";
    } else {
      id = String(first || "");
    }
  }

  // Handle guid being an object (RSS 2.0 with isPermaLink attribute)
  if (typeof id === "object" && id !== null) {
    const obj = id as { _?: string; href?: string; $?: { href?: string } };
    id = obj._ || obj.href || obj.$?.href || "";
  }

  // Handle link being an object (Atom feeds)
  if (typeof id === "object" && id !== null) {
    const obj = id as { href?: string; $?: { href?: string } };
    id = obj.href || obj.$?.href || "";
  }

  return String(id || "");
}

export function extractArticleLink(item: ArticleIdentifiable | null | undefined): string {
  const rawLink = item?.link;
  if (typeof rawLink === "string") {
    return rawLink;
  }

  if (Array.isArray(rawLink) && rawLink.length > 0) {
    const firstLink = rawLink[0];
    if (typeof firstLink === "string") {
      return firstLink;
    }
    if (firstLink && typeof firstLink === "object") {
      const obj = firstLink as { href?: string; $?: { href?: string } };
      return obj.href || obj.$?.href || "";
    }
  }

  if (rawLink && typeof rawLink === "object") {
    const obj = rawLink as { href?: string; $?: { href?: string } };
    return obj.href || obj.$?.href || "";
  }

  return "";
}

export function extractYouTubeVideoId(url: string | null | undefined): string | null {
  if (!url) return null;
  const str = String(url);
  if (!str.includes("youtu") && !str.startsWith("yt:")) return null;

  const match = str.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/|^yt:video:)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1] : null;
}

export function isArticleReadInSet(
  article: ArticleIdentifiable | null | undefined,
  readSet: Set<string> | null | undefined,
): boolean {
  if (!article || !readSet || readSet.size === 0) return false;

  const articleId = normalizeArticleId(article);
  if (articleId && readSet.has(articleId)) return true;

  if (article.guid && typeof article.guid === "string" && readSet.has(article.guid)) {
    return true;
  }

  if (article.id && typeof article.id === "string" && readSet.has(article.id)) {
    return true;
  }

  const link = extractArticleLink(article);
  if (link && readSet.has(link)) {
    return true;
  }

  const hasYouTubeIndicator =
    Boolean(articleId && (articleId.includes("youtu") || articleId.startsWith("yt:"))) ||
    Boolean(link && (link.includes("youtu") || link.startsWith("yt:")));

  if (hasYouTubeIndicator) {
    const videoId = extractYouTubeVideoId(articleId) || extractYouTubeVideoId(link);
    if (videoId) {
      if (readSet.has(`yt:video:${videoId}`)) return true;
      if (readSet.has(`https://www.youtube.com/watch?v=${videoId}`)) return true;
      if (readSet.has(`https://youtu.be/${videoId}`)) return true;
      if (readSet.has(`https://www.youtube.com/shorts/${videoId}`)) return true;
    }
  }

  return false;
}

export function isYouTubeSubscriptionFeedUrl(url: string | undefined): boolean {
  return typeof url === "string" && url.includes("youtube.com/feeds/");
}

export function isYouTubeShortArticle(item: ArticleIdentifiable | null | undefined): boolean {
  const title = String(item?.title || "").toLowerCase();
  const link = extractArticleLink(item).toLowerCase();
  return (
    link.includes("/shorts/") ||
    title.includes("#shorts") ||
    title.includes("#short")
  );
}

export const CLOCK_SKEW_TOLERANCE_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Returns true if an article has a pubDate in the future beyond clock-skew tolerance.
 * Returns false if pubDate is missing, invalid, or in the past/present.
 */
export function isFutureArticle(
  article: ArticleIdentifiable | { pubDate?: unknown } | null | undefined,
  now: number = Date.now(),
  toleranceMs: number = CLOCK_SKEW_TOLERANCE_MS,
): boolean {
  if (!article) return false;
  const rawDate = article.pubDate;
  if (!rawDate) return false;
  if (typeof rawDate !== "string" && !(rawDate instanceof Date) && typeof rawDate !== "number") {
    return false;
  }
  const timestamp = new Date(rawDate).getTime();
  if (isNaN(timestamp)) return false;
  return timestamp > now + toleranceMs;
}

export function sanitizeFeedItemsForSubscriptionUrl<T extends ArticleIdentifiable>(
  subscriptionUrl: string | undefined,
  items: T[] | undefined,
): T[] {
  const normalizedItems = Array.isArray(items) ? items : [];
  const nonFutureItems = normalizedItems.filter((item) => !isFutureArticle(item));
  if (!isYouTubeSubscriptionFeedUrl(subscriptionUrl)) {
    return nonFutureItems;
  }

  return nonFutureItems.filter((item) => !isYouTubeShortArticle(item));
}

// Escape HTML entities
export function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text || "";
  return div.innerHTML;
}

// Extract main article image, skipping avatars and small icons
export function extractMainImage(content: string): string | null {
  if (!content) return null;

  // Patterns that indicate an avatar or author image (to skip)
  const avatarPatterns = [
    /\/avatars?\//i,
    /\/profiles?\/.*\.(jpg|png|gif|webp)/i,
    /gravatar\.com/i,
    /\.gravatar\./i,
    /headshot/i,
    /byline[-_]?img/i,
    /author[-_]?(img|photo|pic|image)/i,
    /user[-_]?(avatar|photo|pic)/i,
  ];

  // Patterns that indicate a main/hero image (preferred)
  const heroPatterns = [
    /hero/i,
    /featured/i,
    /lead[-_]?image/i,
    /article[-_]?image/i,
    /main[-_]?image/i,
    /post[-_]?image/i,
    /cover/i,
    /banner/i,
    /full[-_]?(size|width)/i,
    /product/i,
    /thumbnail/i,
  ];

  // Find all images in content
  const imgRegex = /<img[^>]+>/gi;
  const images = content.match(imgRegex) || [];

  let bestImage = null;
  let bestScore = -1;

  for (const imgTag of images) {
    const srcMatch = imgTag.match(/src=["']([^"']+)["']/i);
    const dataSrcMatch = imgTag.match(/data-(?:lazy-)?src=["']([^"']+)["']/i);
    const srcsetMatch = imgTag.match(/srcset=["']([^\s,"']+)/i);
    const resolvedSrc = srcMatch?.[1] || dataSrcMatch?.[1] || srcsetMatch?.[1];
    if (!resolvedSrc) continue;

    const src = resolvedSrc;
    let score = 0;

    const isLikelyAvatar = avatarPatterns.some(
      (pattern) => pattern.test(src) || pattern.test(imgTag),
    );
    if (isLikelyAvatar) continue;

    const widthMatch = imgTag.match(/width=["']?(\d+)/i);
    const heightMatch = imgTag.match(/height=["']?(\d+)/i);
    if (widthMatch && parseInt(widthMatch[1]) < 50) continue;
    if (heightMatch && parseInt(heightMatch[1]) < 50) continue;

    if (
      heroPatterns.some((pattern) => pattern.test(src) || pattern.test(imgTag))
    ) {
      score += 10;
    }

    const figureCheck = content.indexOf(imgTag);
    const beforeImg = content.substring(
      Math.max(0, figureCheck - 100),
      figureCheck,
    );
    if (/<figure[^>]*>/.test(beforeImg)) {
      score += 5;
    }

    if (widthMatch) {
      const width = parseInt(widthMatch[1]);
      if (width >= 600) score += 3;
      else if (width >= 400) score += 2;
      else if (width >= 200) score += 1;
    }

    if (/cdn|media|images|static|assets/i.test(src)) {
      score += 1;
    }

    if (score > bestScore) {
      bestScore = score;
      bestImage = src;
    }
  }

  if (!bestImage && images.length > 0) {
    for (const imgTag of images) {
      const srcMatch = imgTag.match(/src=["']([^"']+)["']/i);
      const dataSrcMatch = imgTag.match(/data-(?:lazy-)?src=["']([^"']+)["']/i);
      const srcsetMatch = imgTag.match(/srcset=["']([^\s,"']+)/i);
      const resolvedSrc = srcMatch?.[1] || dataSrcMatch?.[1] || srcsetMatch?.[1];
      if (!resolvedSrc) continue;

      const src = resolvedSrc;
      const isLikelyAvatar = avatarPatterns.some(
        (pattern) => pattern.test(src) || pattern.test(imgTag),
      );

      if (!isLikelyAvatar) {
        bestImage = src;
        break;
      }
    }
  }

  return bestImage;
}

// Check if device supports touch
export const isTouchDevice = () => {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  );
};

// Format relative time string for article card display (e.g., "Just now", "5min", "2h", "3d", or date)
export function formatArticleTime(
  date: string | Date | undefined | null,
  now: Date = new Date(),
): string {
  if (!date) return "";
  const pubDate = new Date(date);
  const time = pubDate.getTime();
  if (isNaN(time) || time === 0) return "";

  const diffMs = now.getTime() - time;
  // If the date is significantly in the future (e.g. saved future event), format as date, never negative
  if (diffMs < -CLOCK_SKEW_TOLERANCE_MS) {
    return pubDate.toLocaleDateString();
  }
  // If in the future within skew tolerance or less than 1 minute ago, show "Just now"
  if (diffMs < 60000) {
    return "Just now";
  }

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return pubDate.toLocaleDateString();
}

// Format relative time string (e.g. "Just now", "5m", "2h", "3d", or date)
export function formatTimeAgo(
  date: string | Date | undefined | null,
  now: Date = new Date(),
): string {
  if (!date) return "";
  const pubDate = new Date(date);
  const time = pubDate.getTime();
  if (isNaN(time) || time === 0) return "";

  const diffMs = now.getTime() - time;
  if (diffMs < -CLOCK_SKEW_TOLERANCE_MS) {
    return pubDate.toLocaleDateString();
  }
  if (diffMs < 60000) {
    return "Just now";
  }

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return pubDate.toLocaleDateString();
}

/**
 * Run an async worker across an array of items with a dynamic concurrency limit.
 * As soon as any item completes, the next pending item starts immediately,
 * avoiding head-of-line blocking from slow requests.
 */
export async function runWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  if (!items || items.length === 0) return [];
  const maxConcurrency = Math.max(1, Math.min(limit, items.length));
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from({ length: maxConcurrency }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex++;
      results[currentIndex] = await worker(items[currentIndex]!, currentIndex);
    }
  });

  await Promise.all(workers);
  return results;
}

/**
 * Safely initialize a newly opened window with a styled loading indicator
 * without using the deprecated document.write() API.
 */
export function prepareLoadingWindow(targetWindow: Window | null, title = "Generating AI Summary Card..."): void {
  if (!targetWindow) return;
  try {
    targetWindow.document.title = `RSSio - ${title}`;
    const doc = targetWindow.document;
    if (doc) {
      if (!doc.body && doc.documentElement) {
        doc.documentElement.innerHTML = "<head></head><body></body>";
      }
      if (doc.body) {
        doc.body.style.margin = "0";
        doc.body.style.background = "#0f172a";
        doc.body.style.color = "#e2e8f0";
        doc.body.style.fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
        doc.body.style.display = "flex";
        doc.body.style.flexDirection = "column";
        doc.body.style.alignItems = "center";
        doc.body.style.justifyContent = "center";
        doc.body.style.height = "100vh";

        doc.body.innerHTML = `
          <style>
            .spinner {
              width: 36px;
              height: 36px;
              border: 3px solid rgba(255,255,255,0.2);
              border-top-color: #4c6ef5;
              border-radius: 50%;
              animation: spin 0.8s linear infinite;
              margin-bottom: 16px;
            }
            @keyframes spin { to { transform: rotate(360deg); } }
          </style>
          <div class="spinner"></div>
          <div>${title}</div>
        `;
      }
    }
  } catch {}
}

/**
 * Split an array into chunks of a given maximum size.
 */
export function chunkArray<T>(items: readonly T[], chunkSize: number): T[][] {
  if (!items || items.length === 0) return [];
  if (chunkSize <= 0) return [items.slice()];
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
}


